import { format, isBefore } from 'date-fns';
import { shapeDate } from '@avangard/utils/data';
import {
    BaseEntity,
    Entity as DecribeEntity,
    Relation,
    RelationList,
} from '@avangard/utils/entity';

import { getLocaleForDateFns } from '@lib/i18n';
import { UserEntity, UserEntityList } from '@modules/auth/entities';
import { ChatType } from '@modules/types/graphql';
import { ChatMessageEntityList } from './chat-message.entity-list';

import type { Chat } from '@modules/types/graphql';
import type { ChatMessageEntity } from './chat-message.entity';

export type Entity = Chat | null;

export type ChatMessageGroupByDate = {
    day: string;
    date: Date;
    messages: ChatMessageEntity[];
    hasNewMessages: boolean;
};

@DecribeEntity('ChatEntity')
class ChatEntity extends BaseEntity {
    id: string;
    participantIds: string[];
    type: ChatType;
    createdAt: Date | null;
    updatedAt: Date | null;

    @Relation(() => UserEntity)
    owner: UserEntity;

    @RelationList(() => ChatMessageEntityList)
    messages: (ChatMessageEntity | undefined)[];

    @RelationList(() => UserEntityList)
    participants: UserEntity[];

    constructor(entity: Entity) {
        super(entity);

        this.id = entity?.id ?? '';
        this.type = entity?.type ?? ChatType.default;
        this.participantIds = this.participants.map(participant => participant.id);

        this.createdAt = shapeDate(entity?.createdAt);
        this.updatedAt = shapeDate(entity?.updatedAt);
    }

    getChatTitle(currentUser: UserEntity) {
        if (this.type === ChatType.soa) {
            if (currentUser.isStudent()) {
                return 'Чат по анкете';
            }
        }

        return '';
    }

    getNewMessagesCount() {
        if (this.messages.length === 0) {
            return 0;
        }

        const messages = [...this.messages] as ChatMessageEntity[];

        const result = messages.reduce((carry, message) => {
            if (!message.viewed) {
                carry += 1;
            }

            return carry;
        }, 0);

        return result;
    }

    getLastMessage() {
        if (this.messages.length === 0) {
            return '';
        }

        const messages = ([...this.messages] as ChatMessageEntity[]).filter(
            message => message.sender.id !== this.owner.id,
        );

        messages.sort((a, b) => {
            if (!a.createdAt || !b.createdAt) {
                return 0;
            }

            return isBefore(new Date(a.createdAt), new Date(b.createdAt)) ? 1 : -1;
        });

        return messages[0];
    }

    getGroupedMessageByDate(): ChatMessageGroupByDate[] {
        type messagesByDayMapItem = Record<string, { date: Date; messages: ChatMessageEntity[] }>;

        if (this.messages.length === 0) {
            return [];
        }

        const locale = getLocaleForDateFns();

        const messages = [...this.messages] as ChatMessageEntity[];

        const messagesByDayMap = messages.reduce<messagesByDayMapItem>((carry, message) => {
            if (!message.createdAt) {
                return carry;
            }

            const day = format(new Date(message.createdAt), 'dd.MM.yyyy', { locale });

            if (typeof carry[day] === 'undefined') {
                carry[day] = {
                    date: message.createdAt,
                    messages: [],
                };
            }

            carry[day].messages.push(message);

            return carry;
        }, {});

        const result = Object.entries(messagesByDayMap).map(([day, { date, messages }]) => {
            messages.sort((a, b) => {
                if (!a.createdAt || !b.createdAt) {
                    return 0;
                }

                return isBefore(new Date(a.createdAt), new Date(b.createdAt)) ? -1 : 1;
            });

            const hasNewMessages = messages.some(message => !message.viewed);

            return { day, date, messages, hasNewMessages };
        });

        result.sort((a, b) => (isBefore(new Date(a.date), new Date(b.date)) ? -1 : 1));

        return result;
    }
}

export { ChatEntity };
